import { type ComponentProps, type ReactNode } from "react";

import { Button } from "#app/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#app/components/ui/dialog";
import { Spinner } from "#app/components/ui/spinner";
import { cn } from "#app/utils/misc.tsx";

/**
 * antd's `<Modal>` API on top of the shadcn Dialog.
 *
 * A bridge, deliberately: 41 modals across 30 files lean on antd's
 * `footer` / `okText` / `onOk` / `confirmLoading` conveniences, and
 * hand-restructuring each into `DialogHeader` / `DialogFooter` in one pass is
 * a lot of churn for no visual difference — this already renders the new
 * design. New modals should use `Dialog` directly, and screens should shed
 * this as they are otherwise touched.
 *
 * Differences worth knowing:
 *
 * - `maskClosable={false}` and `keyboard={false}` map onto the Dialog's
 *   dismissal props, so a modal that refused to close on backdrop click still
 *   refuses.
 * - `width` becomes a `max-width`. antd's number is pixels.
 * - `style.top` is dropped. The Dialog is centred, and a literal `top` fights
 *   its `-translate-y-1/2` (see below).
 * - `footer={null}` renders no footer at all, as in antd. Omitting `footer`
 *   gives the default OK/Cancel pair.
 */
/** antd's Button props, as they appear on `okButtonProps` / `cancelButtonProps`. */
type AntButtonProps = Omit<ComponentProps<typeof Button>, "variant"> & {
  danger?: boolean;
  loading?: boolean;
  variant?: ComponentProps<typeof Button>["variant"];
};

function toButtonProps(
  props: AntButtonProps | undefined,
): ComponentProps<typeof Button> {
  if (!props) return {};
  const { danger, loading: _loading, variant, ...rest } = props;
  if (danger) return { ...rest, variant: "destructive" };
  if (variant) return { ...rest, variant };
  return rest;
}

export function Modal({
  open,
  onCancel,
  onOk,
  title,
  description,
  footer,
  okText = "OK",
  cancelText = "Cancel",
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
  width,
  closable = true,
  maskClosable = true,
  keyboard = true,
  className,
  style,
  children,
  afterOpenChange,
  styles,
  afterClose,
}: {
  open?: boolean;
  onCancel?: () => void;
  onOk?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  /** `null` suppresses the footer entirely; a node replaces it. */
  footer?: ReactNode | null;
  okText?: ReactNode;
  cancelText?: ReactNode;
  /** antd's shape, so `{ danger: true }` and `{ loading }` keep working. */
  okButtonProps?: AntButtonProps;
  cancelButtonProps?: AntButtonProps;
  confirmLoading?: boolean;
  /** Pixels, as in antd. Becomes a max-width. */
  width?: number | string;
  centered?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
  afterOpenChange?: (open: boolean) => void;
  /** antd only. The Dialog unmounts its content on close regardless. */
  destroyOnClose?: boolean;
  destroyOnHidden?: boolean;
  /**
   * antd's per-slot style bag. Only `body` is honoured — it lands on the
   * content wrapper, which is where every current caller aims it.
   */
  styles?: { body?: React.CSSProperties };
  /** antd's post-close hook. */
  afterClose?: () => void;
}) {
  const ok = toButtonProps(okButtonProps);
  const cancel = toButtonProps(cancelButtonProps);
  // When the primary action is already destructive (e.g. Remove trigger),
  // Cancel stays neutral outline — don't double up red buttons.
  const okIsDestructive =
    Boolean(okButtonProps?.danger) || ok.variant === "destructive";
  const cancelVariant =
    cancel.variant ?? (okIsDestructive ? "outline" : "destructive");
  // antd's `style={{ top: N }}` offsets a modal from the viewport top. The
  // Dialog centres instead — `fixed top-1/2 -translate-y-1/2` — so a literal
  // `top` overrides the 50% while the transform stays, pulling the box half its
  // own height ABOVE the given offset. Tall modals (the Operator Info API
  // details) lost their title and close button off the top of the screen. There
  // is no honest translation of the antd value here, so it is dropped and the
  // modal centres like every other one.
  const { top: _antdTop, ...safeStyle } = style ?? {};
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel?.();
        afterOpenChange?.(next);
      }}
      onOpenChangeComplete={(next) => {
        if (!next) afterClose?.();
      }}
      // antd's `maskClosable`. Several flows (the subworkflow interrupt, the
      // uninstall confirm) rely on refusing a backdrop click.
      disablePointerDismissal={!maskClosable}
    >
      <DialogContent
        showCloseButton={closable}
        className={cn("max-h-[85dvh] overflow-y-auto", className)}
        style={{
          ...(width
            ? { maxWidth: typeof width === "number" ? `${width}px` : width }
            : {}),
          ...safeStyle,
        }}
        // antd's `keyboard={false}`.
        onKeyDown={
          keyboard
            ? undefined
            : (event) => {
                if (event.key === "Escape") event.preventDefault();
              }
        }
      >
        {title || description ? (
          <DialogHeader>
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
        ) : null}

        {styles?.body ? <div style={styles.body}>{children}</div> : children}

        {footer === null ? null : footer !== undefined ? (
          <DialogFooter>{footer}</DialogFooter>
        ) : (
          <DialogFooter>
            <DialogClose
              render={
                <Button {...cancel} variant={cancelVariant}>
                  {cancelText}
                </Button>
              }
            />
            <Button
              onClick={onOk}
              {...ok}
              variant={ok.variant ?? "gradient"}
              disabled={confirmLoading || okButtonProps?.loading || ok.disabled}
            >
              {confirmLoading || okButtonProps?.loading ? <Spinner /> : null}
              {okText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default Modal;
